import React from 'react';

const DayOneBenchmark = ({ navigateTo }) => {
  return (
    <div className="bg-black pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">
        {/* Back button */}
        <button 
          onClick={() => navigateTo('blog-title')} 
          className="mb-8 flex items-center text-gray-400 hover:text-white transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Blog
        </button>

        {/* Blog post header */}
        <header className="mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-4">Day One Benchmark From HumanAI: Promising Signals, Big Potential</h1>
          <div className="flex items-center text-sm text-gray-400 mb-6">
            <span>Apr 20, 2025</span>
            <span className="mx-2">•</span>
            <span>7 min read</span>
            <span className="mx-2">•</span>
            <span>Technology</span>
          </div>
          <div className="flex items-center">
            <img 
              src={process.env.PUBLIC_URL + '/img/sharif-headshot.jpg'} 
              alt="Dr. Sharif Amlani" 
              className="h-10 w-10 rounded-full mr-3"
            />
            <div>
              <p className="text-white font-medium">Dr. Sharif Amlani</p>
              <p className="text-gray-400 text-sm">Founder & CEO</p>
            </div>
          </div>
        </header>

        {/* Blog content */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-gray-300 mb-6">
            Can large language models simulate real human survey respondents? Inspired by a recent benchmark study, I ran our very first test to find out—and the early signs are exciting.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Why This Benchmark?</h2>
          <div className="space-y-6">
            <p>
                A recent study by{" "}
                <a
                href="https://doi.org/10.1017/pan.2024.5"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-pink-400 hover:text-pink-300"
                >
                Bisbee et al. (2024)
                </a>{" "}
                systematically evaluated whether large language models (LLMs), like ChatGPT, can serve as synthetic replacements for human survey respondents. Using a sample of over 3.6 million responses, the authors prompted ChatGPT-3.5 Turbo to simulate individuals from the American National Election Study (ANES) by adopting matched demographic and political “personas,” then provide feeling thermometer ratings toward sociopolitical groups.
            </p>

            <p>
                Their findings are nuanced. On one hand, the AI-generated group-level averages closely mirrored actual human survey data, showing impressive alignment in overall sentiment distributions. On the other hand, the synthetic data fell short in key scientific areas: it underestimated individual-level variance, distorted regression relationships between demographics and opinions, and was highly sensitive to prompt wording, model version, and even run timing. For example, 48% of regression coefficients differed significantly from real ANES-based estimates, with some even reversing sign—suggesting the model may point researchers toward incorrect conclusions.
            </p>

            <p>
                Perhaps most concerning, the same prompt produced different outputs across time as OpenAI updated its model, raising alarms about reproducibility and transparency in LLM-based research. The authors conclude that while LLMs show early promise in summarizing broad opinion trends, they are not yet reliable tools for causal inference or generalizable social science. Their work is both a caution and a foundation—highlighting where synthetic data works, and where it still breaks.
            </p>
            </div>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Methodology</h2>
          <ul>
            <li><strong>Model Used:</strong> GPT-4o mini</li>
            <li><strong>Sample Size:</strong> 5,000 AI respondents</li>
            <li><strong>Prompt:</strong> Bisbee’s original ANES-inspired prompt</li>
            <li><strong>Focus:</strong> Feeling thermometer scores toward 16 key groups</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Results: AI vs. Actual Group Means</h2>
          <p> 
            The table below compares the mean response from AI vs. the actual survey data, the difference between them, and whether the difference is statistically significant. While the differences are statistically significant in most cases, many are surprisingly small. 
          </p>

          <div className="overflow-x-auto mt-6">
            <table className="table-auto w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-600 text-left">
                  <th className="pb-2 pr-6">Group</th>
                  <th className="pb-2 pr-6">AI Mean</th>
                  <th className="pb-2 pr-6">Actual Mean</th>
                  <th className="pb-2 pr-6">Difference</th>
                  <th className="pb-2">Significant?</th>
                </tr>
              </thead>
              <tbody>
                {[ 
                  ["Asian-Americans", 73.77, 71.58, "+2.19", "Yes"],
                  ["Blacks", 71.11, 72.21, "-1.10", "Yes"],
                  ["Christians", 73.54, 71.87, "+1.67", "Yes"],
                  ["Conservatives", 60.78, 54.42, "+6.36", "Yes"],
                  ["Democratic Party", 58.23, 45.26, "+12.97", "Yes"],
                  ["Gay Men and Lesbians", 66.84, 65.40, "+1.43", "Yes"],
                  ["Hispanics", 68.81, 71.92, "-3.11", "Yes"],
                  ["Jews", 73.78, 73.48, "+0.30", "No"],
                  ["Liberals", 57.58, 50.61, "+6.96", "Yes"],
                  ["Muslims", 60.17, 58.54, "+1.62", "Yes"],
                  ["Republican Party", 56.02, 43.96, "+12.06", "Yes"],
                  ["Whites", 71.42, 70.21, "+1.22", "Yes"]
                ].map(([group, ai, actual, diff, sig], idx) => (
                  <tr key={idx} className="border-b border-gray-700">
                    <td className="py-2 pr-6">{group}</td>
                    <td className="py-2 pr-6">{ai}</td>
                    <td className="py-2 pr-6">{actual}</td>
                    <td className="py-2 pr-6">{diff}</td>
                    <td className="py-2">{sig === "Yes" ? "✅" : "❌"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Visualizing the Results</h2>
          <img 
            src={process.env.PUBLIC_URL + '/img/AI_vs_Human_Thermometer_Scores.png'} 
            alt="AI vs Human Thermometer Scores"
            className="rounded-xl shadow-xl mt-4"
          />

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Key Takeaways</h2>
          <ul>
            <li><strong>✅ Group-level accuracy:</strong> AI closely approximates group means on most items.</li>
            <li><strong>⚠️ Variance limitations:</strong> Model responses are tightly clustered—failing to capture the full distribution of human sentiment.</li>
            <li><strong>🚀 Useful in practice:</strong> These results show AI can serve as a fast, affordable proxy for large-scale survey testing—especially at the group level.</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Next Steps</h2>
          <ol className="list-decimal ml-6 space-y-2">
            <li>
              <strong>Calibrate.</strong> Apply post‑hoc variance inflation and bias correction against held‑out human data.
            </li>
            <li>
              <strong>Prompt engineering 2.0.</strong> Condition on ideology and probability weights to better match real‑world dispersion.
            </li>
            <li>
              <strong>Model bake‑off.</strong> Compare GPT‑4o, Claude 3, Mistral‑Large, and a local LLaMA‑3 instance to see which generalizes best.
            </li>
            <li>
              <strong>Robustness.</strong> Test multi‑wave stability and replicate the study outside the U.S. cultural context.
            </li>
          </ol>

          <p className="mt-6">
            Have thoughts, questions, or ideas for collaboration? Let's talk.
          </p>

          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Try Synthetic Respondents?</h3>
                <p className="text-gray-300">Experience the future of market research with HumanAI's synthetic respondent technology.</p>
              </div>
              <a 
                href="https://www.humanaienterprise.com/auth" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 whitespace-nowrap"
              >
                Use It Free
              </a>
            </div>
          </div>

          <p className="mt-6 font-semibold">— Sharif From HumanAI</p>
        </div>
      </div>
    </div>
  );
};

export default DayOneBenchmark;
